import { GraphModel } from '../../graph/graph.model';
import { DatasetModel } from '../model/dataset.model';
import { z } from 'zod/v3';
import {
  LoadingToastController,
  toast,
} from '@cotera/client/app/components/ui/toaster';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { Assert } from '@cotera/utilities';
import { DATASET_ACTION_GROUP } from './constants';
import { AskResponse } from '../../actions/framework/types';
import { useState } from 'react';
import { Loading } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Label } from '@cotera/client/v0/components/ui/label';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  makeDefaultFilterGroup,
  ModalPageView,
} from '@cotera/client/app/components/app';
import { CreateViewAction } from './create-view.action';

type Params = {};

export class SaveDatasetAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Save Changes';
  description = 'Stage current changes for this dataset';
  inputSchema = z.object({});
  shortcut = ['ctrl', 's'];
  group = DATASET_ACTION_GROUP;
  icon = 'save';

  constructor(
    private readonly navigator: {
      pushAndReplace: (props: { route: string }) => void;
    },
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      close: () => void;
    }
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return (
      context.isInScope(GraphModel) &&
      context.hasTargetedModelOfType(DatasetModel)
    );
  }

  async execute(
    _: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const graph = context.getInScopeModelOfType(GraphModel);
    const dataset = context.targetedModelOfType(DatasetModel);

    Assert.assert(graph !== null, 'Graph not found');
    Assert.assert(dataset !== null, 'Dataset not found');

    const isNew = dataset.isNew; // must be defined up here, as below, isNew might have updated after the save

    const saveToast = toast.loading('Saving changes...', {
      id: 'save-dataset',
    });

    const result = dataset.compile({ setPreview: false });

    if (result.isErr()) {
      saveToast.error(JSON.stringify(result.error));
      return err([
        { t: result.error.t, message: JSON.stringify(result.error.e) },
      ]);
    }

    const saveOperationsResult = await this.saveOperations(
      dataset,
      saveToast,
      graph,
      isNew
    );

    if (saveOperationsResult.isErr()) {
      return saveOperationsResult;
    }

    const route = dataset.route.snapshot();
    // The route should already be properly encoded, but let's ensure it is
    this.navigator.pushAndReplace({ route });

    const filterHasChanged = dataset.changes.snapshot().where;

    if (filterHasChanged) {
      const result = await context
        .getAction(CreateViewAction)
        .askOrExecute({}, context);

      if (result.isErr()) {
        saveToast.error(JSON.stringify(result.error));
        return err(result.error);
      }

      if (!result.value.cancelled) {
        //reset the filter group
        dataset.filterGroup.set(makeDefaultFilterGroup());
      }
    }

    return ok({});
  }

  shouldAsk(context: ApplicableContext): boolean {
    const model = context.targetedModelOfType(DatasetModel)!;

    return !model.formState.snapshot().name || model.isNew;
  }

  ask(_: {}, context: ExecuteContext): AskResponse<Params> {
    const model = context.targetedModelOfType(DatasetModel);

    Assert.assert(model !== null);

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <NewAskView
                onRespond={async (params) => {
                  await onRespond(params);
                  this.modalManager.close();
                }}
                model={model}
              />
            );
          },
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }

  private async saveOperations(
    dataset: DatasetModel,
    saveToast: LoadingToastController,
    graph: GraphModel,
    isNew: boolean
  ) {
    if (dataset.newOperations.snapshot().length === 0) {
      saveToast.done();
      return ok({});
    }

    const stagingResult = await graph.attemptToStageOperations(
      dataset.newOperations.snapshot()
    );

    if (stagingResult.isErr()) {
      const error = {
        t: stagingResult.error.err.t,
        message: JSON.stringify(stagingResult.error.err),
      };

      saveToast.error(error.message);
      return err([error]);
    }

    /** If this is a new dataset, update the dataset's identity.  */
    if (isNew) {
      Assert.assert(dataset.currentNameInGraph.snapshot() === null);
      Assert.assert(dataset.datasetIdentity.snapshot() === null);

      const datasetName = dataset.formState.snapshot().name;
      const datasetNode = graph.graph.snapshot().nodeMap[datasetName];
      Assert.assert(datasetNode !== undefined); // must exist if compiles
      dataset.datasetIdentity.set(datasetNode.meta.identity);
    }

    dataset.markAsStaged();

    saveToast.success(`Changes saved to ${dataset.formState.snapshot().name}`);

    return ok({});
  }
}

const NewAskView: React.FC<{
  onRespond: (params: Params) => Promise<void>;
  model: DatasetModel;
}> = ({ onRespond, model }) => {
  const [name, setName] = useState(model.formState.snapshot().name);
  const [loading, setLoading] = useState(false);

  return (
    <ModalPageView
      title={`Save ${model.formState.snapshot().name}`}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="dataset-name" className="text-sm">
            Dataset Name
          </Label>
          <Input
            id="dataset-name"
            placeholder="name"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="w-full flex justify-end">
          <Button
            disabled={loading}
            type="submit"
            variant="default"
            icon="save"
            className="w-fit self-end"
            onClick={async () => {
              setLoading(true);
              model.formState.set({
                ...model.formState.snapshot(),
                name,
              });
              await onRespond({});
              setLoading(false);
            }}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Save'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
